"use client";

import { c as _c } from "react/compiler-runtime";
import { ReactNode, useMemo } from "react";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { useFrontPage } from "@/hooks/self-schedule/use-front-page";
import { ServicesData, ServicesProvider, useServicesContextValue } from "./services-context";
import { FIRST_AVAILABLE_PROVIDER_ID, useSelfScheduleStore } from "@/stores/self-schedule-store";

/**
 * Adapter: exposes the self-schedule context as a ServicesContext.
 * The real self-schedule flow wraps its service-step UI with this provider so
 * the UI reads from a focused interface while the underlying state still lives
 * in the global self-schedule store.
 */
export function SelfScheduleServicesProvider(t0) {
  const $ = _c(47);
  const {
    children
  } = t0;
  const {
    serviceSearchQuery,
    setServiceSearchQuery,
    selectedCategory,
    setSelectedCategory,
    locationType,
    patientType,
    userLocation,
    selectedLocation,
    requestUserLocation,
    setSelectedLocationId,
    addSelectedService,
    removeSelectedService,
    isServiceSelected,
    getServiceInstanceCount,
    selectedServices,
    selectedServiceInstances,
    effectiveNumAllowedServices
  } = useSelfScheduleContext();
  const storeSelectedProviderId = useSelfScheduleStore(_temp);
  const removeAllServiceInstances = useSelfScheduleStore(_temp2);
  const selectedProviderId = storeSelectedProviderId === FIRST_AVAILABLE_PROVIDER_ID ? null : storeSelectedProviderId;
  const {
    data: frontPage,
    isLoading
  } = useFrontPage();
  let t1;
  bb0: {
    if (!frontPage) {
      t1 = undefined;
      break bb0;
    }
    let t2;
    if ($[0] !== frontPage.categories || $[1] !== frontPage.locations || $[2] !== frontPage.providers || $[3] !== frontPage.serviceDisplayMode || $[4] !== frontPage.services || $[5] !== frontPage.shouldGroupServicesByCategory || $[6] !== frontPage.shouldGroupServicesByLocation || $[7] !== frontPage.showServiceDuration) {
      t2 = {
        services: frontPage.services,
        categories: frontPage.categories,
        locations: frontPage.locations,
        providers: frontPage.providers,
        shouldGroupServicesByCategory: frontPage.shouldGroupServicesByCategory,
        shouldGroupServicesByLocation: frontPage.shouldGroupServicesByLocation,
        serviceDisplayMode: frontPage.serviceDisplayMode,
        showServiceDuration: frontPage.showServiceDuration
      };
      $[0] = frontPage.categories;
      $[1] = frontPage.locations;
      $[2] = frontPage.providers;
      $[3] = frontPage.serviceDisplayMode;
      $[4] = frontPage.services;
      $[5] = frontPage.shouldGroupServicesByCategory;
      $[6] = frontPage.shouldGroupServicesByLocation;
      $[7] = frontPage.showServiceDuration;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = t2;
  }
  const data = t1;
  let t2;
  if ($[9] !== selectedServices) {
    t2 = selectedServices ?? [];
    $[9] = selectedServices;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t2) {
    t3 = t2.map(_temp3);
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const selectedServiceIds = t3;
  const t4 = selectedCategory || undefined;
  const t5 = selectedLocation?.id ?? null;
  let t6;
  if ($[13] !== selectedServices) {
    t6 = selectedServices ?? [];
    $[13] = selectedServices;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== data || $[16] !== effectiveNumAllowedServices || $[17] !== isLoading || $[18] !== locationType || $[19] !== patientType || $[20] !== selectedLocation || $[21] !== selectedProviderId || $[22] !== selectedServiceIds || $[23] !== serviceSearchQuery || $[24] !== t4 || $[25] !== t5 || $[26] !== t6 || $[27] !== userLocation) {
    t7 = {
      searchQuery: serviceSearchQuery,
      selectedCategory: t4,
      locationType,
      patientType,
      selectedLocationId: t5,
      userLocation,
      selectedServiceIds,
      selectedServices: t6,
      effectiveNumAllowedServices,
      selectedProviderId,
      selectedLocation,
      data,
      isLoading
    };
    $[15] = data;
    $[16] = effectiveNumAllowedServices;
    $[17] = isLoading;
    $[18] = locationType;
    $[19] = patientType;
    $[20] = selectedLocation;
    $[21] = selectedProviderId;
    $[22] = selectedServiceIds;
    $[23] = serviceSearchQuery;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = userLocation;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== setSelectedCategory) {
    t8 = c => setSelectedCategory(c ?? "");
    $[29] = setSelectedCategory;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== removeSelectedService || $[32] !== selectedServiceInstances) {
    t9 = serviceId => {
      const instance = [...selectedServiceInstances].reverse().find(s_0 => s_0.id === serviceId);
      if (instance) {
        removeSelectedService(instance.instanceId);
      }
    };
    $[31] = removeSelectedService;
    $[32] = selectedServiceInstances;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== addSelectedService || $[35] !== getServiceInstanceCount || $[36] !== isServiceSelected || $[37] !== removeAllServiceInstances || $[38] !== requestUserLocation || $[39] !== setSelectedLocationId || $[40] !== setServiceSearchQuery || $[41] !== t8 || $[42] !== t9) {
    t10 = {
      setSearchQuery: setServiceSearchQuery,
      setSelectedCategory: t8,
      addService: addSelectedService,
      removeService: t9,
      removeAllServiceInstances,
      isServiceSelected,
      getServiceInstanceCount,
      requestUserLocation,
      setSelectedLocationId
    };
    $[34] = addSelectedService;
    $[35] = getServiceInstanceCount;
    $[36] = isServiceSelected;
    $[37] = removeAllServiceInstances;
    $[38] = requestUserLocation;
    $[39] = setSelectedLocationId;
    $[40] = setServiceSearchQuery;
    $[41] = t8;
    $[42] = t9;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  const value = useServicesContextValue(t7, t10);
  let t11;
  if ($[44] !== children || $[45] !== value) {
    t11 = <ServicesProvider value={value}>{children}</ServicesProvider>;
    $[44] = children;
    $[45] = value;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  return t11;
}
function _temp3(s) {
  return s.id;
}
function _temp2(state_0) {
  return state_0.removeAllInstancesOfService;
}
function _temp(state) {
  return state.selectedProviderId;
}